import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, fmtNGN, img } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MoneyInput } from "@/components/MoneyInput";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Gavel, Clock, Trophy } from "lucide-react";
import { motion } from "framer-motion";

export default function AuctionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const [bid, setBid] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/auctions/${id}`).then((r) => setA(r.data)).catch(() => nav("/auctions"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const placeBid = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/auctions/${id}/bid`, { amount: Number(bid) });
      toast.success(`Bid placed: ${fmtNGN(data.current_bid)}`);
      setBid("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!a) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  const minBid = a.current_bid + a.min_increment;

  return (
    <div>
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Back to auctions</button>
      <div className="grid lg:grid-cols-2 gap-8">
        <motion.img initial={{ opacity: 0 }} animate={{ opacity: 1 }} src={img(a.image)} alt={a.title} className="w-full rounded-3xl border border-border object-contain max-h-[70vh] bg-muted" />
        <div>
          <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-gold bg-gold/10 px-3 py-1.5 rounded-full"><Clock className="h-3 w-3" /> {a.status === "completed" ? "Completed" : "Live Auction"}</span>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mt-3">{a.title}</h1>
          <p className="text-muted-foreground">Listed by {a.seller_username} • {a.artist}</p>

          <div className="bg-card border border-border rounded-3xl p-6 mt-6">
            <p className="text-sm text-muted-foreground">{a.status === "completed" ? "Final price" : "Current top bid"} {a.top_bidder ? `• ${a.top_bidder}` : ""}</p>
            <p className="font-heading text-4xl font-bold text-gold" data-testid="auction-current-bid">{fmtNGN(a.status === "completed" ? (a.final_price || a.current_bid) : a.current_bid)}</p>
            {a.status === "live" ? (
              <>
                <div className="flex gap-2 mt-5">
                  <MoneyInput value={bid} onValueChange={setBid} placeholder={`Min ${fmtNGN(minBid)}`} className="h-12 rounded-full" data-testid="bid-input" />
                  <Button onClick={placeBid} disabled={busy || Number(bid) < minBid} data-testid="place-bid-btn" className="h-12 px-6 rounded-full navy-gradient text-white font-semibold whitespace-nowrap">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Gavel className="h-4 w-4 mr-2" /> Bid</>}
                  </Button>
                </div>
              </>
            ) : (
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><Trophy className="h-4 w-4 text-gold" /> Won by {a.winner || "no bidder"}.</div>
            )}
          </div>

          <div className="mt-6">
            <h3 className="font-heading text-lg font-bold mb-3">Bid History</h3>
            {a.bids?.length ? (
              <div className="space-y-2" data-testid="bid-history">
                {a.bids.map((b, i) => (
                  <div key={b.id} className="bg-card border border-border rounded-2xl p-3 flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">{i === 0 && <Trophy className="h-4 w-4 text-gold" />} {b.username}</span>
                    <span className="font-bold">{fmtNGN(b.amount)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No bids yet. Be the first!</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
